import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auditFetch, buildAuditHeaders } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const sessionStatusLabels = {
  created: 'Creada',
  active: 'En atencion',
  finished: 'Finalizada',
};

const eventLabels = {
  livekit_token_issued: 'Token emitido',
  participant_joined: 'Participante ingreso',
  participant_left: 'Participante salio',
  room_started: 'Sala iniciada',
  room_finished: 'Sala finalizada',
  recording_auto_started: 'Grabacion automatica iniciada',
  recording_started: 'Grabacion iniciada',
  recording_stopped: 'Grabacion detenida',
  recording_start_failed: 'Error al iniciar grabacion',
  recording_skipped_no_consent: 'Grabacion omitida por falta de consentimiento',
  egress_started: 'Egress iniciado',
  egress_ended: 'Egress finalizado',
  egress_failed: 'Egress fallido',
};

const TeleconsultationAuditPanel = ({ user }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [filters, setFilters] = useState({ doctorId: '', patientId: '', status: '', dateFrom: '', dateTo: '' });
  const [sessions, setSessions] = useState([]);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState('');
  const [events, setEvents] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const auditActor = useMemo(() => ({
    id: user.id,
    role: user.role,
    name: user.name,
  }), [user.id, user.role, user.name]);

  const loadSessions = useCallback(() => {
    setIsLoading(true);
    setStatusMessage('');
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    auditFetch(`${backendUrl}/api/admin/teleconsultations/audit?${params.toString()}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudo cargar auditoria'))))
      .then((data) => setSessions(Array.isArray(data.sessions) ? data.sessions : []))
      .catch((error) => {
        console.error('Error loading teleconsultation audit:', error);
        setStatusMessage('No se pudo cargar auditoria de teleconsultas.');
      })
      .finally(() => setIsLoading(false));
  }, [auditActor, backendUrl, filters]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const loadEvents = (appointmentId) => {
    setSelectedAppointmentId(appointmentId);
    auditFetch(`${backendUrl}/api/admin/teleconsultations/${appointmentId}/events`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudo cargar bitacora'))))
      .then((data) => setEvents(Array.isArray(data.events) ? data.events : []))
      .catch((error) => {
        console.error('Error loading teleconsultation events:', error);
        setStatusMessage('No se pudo cargar la bitacora de eventos.');
      });
  };

  const openRecording = (recording, mode) => {
    if (!recording?.id) return;
    const endpoint = `${backendUrl}/api/admin/recordings/${recording.id}/${mode}`;
    fetch(endpoint, { headers: buildAuditHeaders(auditActor) })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo abrir la grabacion');
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch((error) => {
        console.error('Error opening recording:', error);
        setStatusMessage('No se pudo abrir la grabacion. Verifica si el archivo existe en storage local.');
      });
  };

  return (
    <section className="teleconsultation-audit-panel">
      <div className="section-header">
        <div>
          <h2>Auditoria de teleconsultas</h2>
          <p>Sesiones LiveKit, eventos, grabaciones y accesos protegidos.</p>
        </div>
        <button className="btn btn-secondary" type="button" onClick={loadSessions} disabled={isLoading}>
          {isLoading ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      <div className="teleconsultation-audit-filters">
        <input value={filters.doctorId} onChange={(event) => setFilters((current) => ({ ...current, doctorId: event.target.value }))} placeholder="Medico ID" />
        <input value={filters.patientId} onChange={(event) => setFilters((current) => ({ ...current, patientId: event.target.value }))} placeholder="Paciente ID" />
        <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
          <option value="">Todos los estados</option>
          <option value="created">Creada</option>
          <option value="active">En atencion</option>
          <option value="finished">Finalizada</option>
        </select>
        <input type="date" value={filters.dateFrom} onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))} />
        <input type="date" value={filters.dateTo} onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))} />
      </div>

      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}

      <div className="teleconsultation-audit-grid">
        <div className="teleconsultation-session-list">
          {sessions.length === 0 ? (
            <p className="audit-empty">Sin sesiones LiveKit registradas.</p>
          ) : sessions.map((session) => (
            <article key={session.id} className="teleconsultation-session-card">
              <div>
                <strong>{session.patientName || session.patientId || 'Paciente'} · {session.doctorName || session.doctorId || 'Medico'}</strong>
                <span>{session.roomName} · {sessionStatusLabels[session.status] || session.status}</span>
                <small>{session.startedAt ? new Date(session.startedAt).toLocaleString('es-EC') : 'Inicio pendiente'} · {session.providerKind || 'provider'}</small>
              </div>
              <div className="teleconsultation-session-actions">
                <button type="button" onClick={() => loadEvents(session.appointmentId)}>Bitacora</button>
                {session.recording ? (
                  <>
                    <button type="button" onClick={() => openRecording(session.recording, 'view')}>Ver video</button>
                    <button type="button" onClick={() => openRecording(session.recording, 'download')}>Descargar</button>
                  </>
                ) : (
                  <span>Sin video</span>
                )}
              </div>
            </article>
          ))}
        </div>

        <div className="teleconsultation-event-list">
          <h3>Bitacora {selectedAppointmentId ? `· ${selectedAppointmentId}` : ''}</h3>
          {events.length === 0 ? (
            <p className="audit-empty">Selecciona una sesion para ver eventos.</p>
          ) : events.map((event) => (
            <div key={event.id} className={`teleconsultation-event-item event-${event.source}`}>
              <strong>{eventLabels[event.eventType] || event.eventType}</strong>
              <span>{event.actorRole || event.source} {event.actorId ? `· ${event.actorId}` : ''}</span>
              <small>{new Date(event.createdAt).toLocaleString('es-EC')}</small>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeleconsultationAuditPanel;
